import type { JournalPersonCardData, JournalPersonnelSectionType } from "./types";

const RESPONSIBLE_ROLE_LABELS: Record<string, string> = {
  inputters: "Заполнение данных",
  approvers: "Согласующий",
  signers: "Подписант",
  senders: "Отправка в ГСН",
};

const SECTION_RESPONSIBLE_KEYS: Record<
  JournalPersonnelSectionType,
  readonly string[]
> = {
  sectionOne: ["inputters", "signers"],
  sectionTwo: ["inputters", "approvers", "signers", "senders"],
  sectionThree: ["inputters", "approvers", "signers", "senders"],
  sectionFour: ["inputters", "approvers", "signers", "senders"],
};

type UserLike = {
  userSurname?: string;
  userFirstName?: string;
  userMiddleName?: string;
  surname?: string;
  firstName?: string;
  middleName?: string;
  userId?: string;
  userEmail?: string;
  userMobilePhone?: string;
};

export const getPersonInitials = (user?: UserLike | null) => {
  const firstName = user?.userFirstName ?? user?.firstName ?? "";
  const middleName = user?.userMiddleName ?? user?.middleName ?? "";
  const surname = user?.userSurname ?? user?.surname ?? "";

  const fromNames = `${firstName[0] ?? ""}${middleName[0] ?? ""}`.trim();
  if (fromNames) return fromNames;

  return `${surname[0] ?? ""}${firstName[0] ?? ""}`.trim() || "-";
};

export const formatPersonName = (user?: UserLike | null) => {
  const surname = user?.userSurname ?? user?.surname ?? "";
  const firstName = user?.userFirstName ?? user?.firstName ?? "";
  const middleName = user?.userMiddleName ?? user?.middleName ?? "";

  if (surname && firstName) {
    const initials = `${firstName[0]}.${middleName ? `${middleName[0]}.` : ""}`;
    return `${surname} ${initials}`.trim();
  }

  return [surname, firstName, middleName].filter(Boolean).join(" ").trim() || "-";
};

const getCompanyInfo = (userHistory?: {
  roleInfo?: { userCompaniesHistory?: Array<{ shortName?: string; position?: string }> };
}) => {
  const company = userHistory?.roleInfo?.userCompaniesHistory?.[0];
  return {
    organization: company?.shortName || "-",
    position: company?.position || "-",
  };
};

const uniqueBy = <T>(items: T[], getKey: (item: T) => string) => {
  const map = new Map<string, T>();
  items.forEach((item) => {
    const key = getKey(item);
    if (!map.has(key)) {
      map.set(key, item);
    }
  });
  return Array.from(map.values());
};

export const mapSignersToCards = (signers: any[] | undefined): JournalPersonCardData[] => {
  if (!signers?.length) return [];

  const cardsMap = new Map<string, JournalPersonCardData>();

  signers.forEach((signer) => {
    const userInfo = signer?.userInfo;
    const name = formatPersonName(userInfo);
    const role = signer?.role ?? signer?.representativeRole ?? "-";
    const key = `${name}|${role}`;

    const existing = cardsMap.get(key);
    const pendingIncrement =
      signer?.status === "AWAITING_SIGNATURE" ||
      signer?.status === "PENDING" ||
      signer?.status === "NEW"
        ? 1
        : 0;
    const signedIncrement =
      signer?.status === "SIGNED" || signer?.status === "DONE" ? 1 : 0;

    if (existing) {
      existing.pendingCount = (existing.pendingCount ?? 0) + pendingIncrement;
      existing.signedCount = (existing.signedCount ?? 0) + signedIncrement;
      return;
    }

    cardsMap.set(key, {
      id: key,
      userId: userInfo?.userId,
      name,
      initials: getPersonInitials(userInfo),
      organization: signer?.organization ?? signer?.companyName ?? "-",
      subtitle: role,
      pendingCount: pendingIncrement || undefined,
      signedCount: signedIncrement || undefined,
    });
  });

  return Array.from(cardsMap.values());
};

export const mapResponsibleToCards = (
  data: Record<string, any[] | undefined> | undefined,
  sectionType: JournalPersonnelSectionType,
): JournalPersonCardData[] => {
  if (!data) return [];

  const cards = SECTION_RESPONSIBLE_KEYS[sectionType].flatMap((roleKey) =>
    (data[roleKey] ?? []).map((item) => {
      const user = item?.userHistory ?? {};
      const { organization, position } = getCompanyInfo(user);
      const roleLabel = RESPONSIBLE_ROLE_LABELS[roleKey] ?? roleKey;

      return {
        id: `${formatPersonName(user)}|${roleLabel}`,
        userId: user.userId,
        name: formatPersonName(user),
        initials: getPersonInitials(user),
        organization,
        subtitle: position,
        roleTags: [roleLabel],
        email: user.userEmail || "-",
        phone: user.userMobilePhone || "-",
      } satisfies JournalPersonCardData;
    }),
  );

  return uniqueBy(cards, (card) => card.id);
};
